import { Navigate, useParams } from "react-router-dom";
import AccessControl from "../pages/AccessControl";
import ChatSpace from "../pages/ChatSpace";
import EmailMessages from "../pages/EmailMessages";
import Feedback from "../pages/Feedback";
import Help from "../pages/Help";
import Invitations from "../pages/Invitations";
import OrganizationDetailsPage from "../pages/OrganizationDetailsPage";
import OrganizationsPage from "../pages/OrganizationsPage";
import PartnerCommissions from "../pages/PartnerCommissions";
import Partners from "../pages/Partners";
import Settings from "../pages/Settings";
import Support from "../pages/Support";
import WebsiteCreationRedirect from "../pages/WebsiteCreationRedirect";
import WebsiteDetails from "../pages/WebsiteDetails";
import UserProfilePage from "../users/profile/UserProfilePage";
import DashboardPage from "../pages/care/DashboardPage";
import HomePage from "../pages/care/HomePage";
import CareStaffSchedulesPage from "../pages/care/CareStaffSchedulesPage";
import CustomerInquiriesPage from "../pages/care/CustomerInquiriesPage";
import ReportsPage from "../pages/care/ReportsPage";
import SiteSettingsPage from "../pages/care/SiteSettingsPage";
import VehicleNewPage from "../pages/care/VehicleNewPage";
import VehicleDetailPage from "../pages/care/VehicleDetailPage";
import VehiclesListPage from "../pages/care/VehiclesListPage";
import WorkspaceChargesPage from "../workspaces/WorkspaceChargesPage";
import WorkspaceDetailsPage from "../workspaces/WorkspaceDetailsPage";
import WorkspaceDownloadsPage from "../workspaces/WorkspaceDownloadsPage";
import WorkspaceInvitationAcceptPage from "../workspaces/WorkspaceInvitationAcceptPage";
import WorkspaceInvitations from "../workspaces/WorkspaceInvitations";
import WorkspaceMemberDetailsPage from "../workspaces/WorkspaceMemberDetailsPage";
import WorkspaceOrganizationPage from "../workspaces/WorkspaceOrganizationPage";
import WorkspacePaymentMethodsPage from "../workspaces/WorkspacePaymentMethodsPage";
import WorkspaceSubscriptionsPage from "../workspaces/WorkspaceSubscriptionsPage";
import WorkspacesPage from "../workspaces/WorkspacesPage";
import { CareShell } from "../components/care/CareLayout";
import RequireCareRole from "../components/routing/RequireCareRole";
import { useWorkspace } from "../contexts/WorkspaceContext";

function CurrentWorkspaceRedirect({ section }) {
  const { selectedWorkspaceId, loading, workspaceListReady } = useWorkspace();

  if (loading || !workspaceListReady) return null;
  if (!selectedWorkspaceId) return <Navigate to="/workspaces" replace />;
  return (
    <Navigate to={`/workspaces/${selectedWorkspaceId}/${section}`} replace />
  );
}

function RequireWorkspaceMembership({ children }) {
  const { workspaceId } = useParams();
  const { workspaces, loading, workspaceListReady } = useWorkspace();

  if (loading || !workspaceListReady) return null;

  const isMemberWorkspace = workspaces.some(
    (workspace) => workspace?.id === workspaceId,
  );

  if (!isMemberWorkspace) {
    return <Navigate to="/workspaces" replace />;
  }

  return children;
}

function ShellRoute({ children }) {
  return (
    <CareShell className="dashboard-content app-page-content">
      {children}
    </CareShell>
  );
}

function OwnerAdminRoute({ children }) {
  return (
    <RequireCareRole roles={["care_owner", "care_admin", "auto_owner", "auto_admin"]}>
      {children}
    </RequireCareRole>
  );
}

const ownerAdmin = (element) => <OwnerAdminRoute>{element}</OwnerAdminRoute>;

const workspaceMemberOnly = (element) => (
  <RequireWorkspaceMembership>{element}</RequireWorkspaceMembership>
);

function NavigateToNewAppraisal() {
  return <Navigate to="/vehicles" replace />;
}

function NavigateToCurrentAppraisal() {
  return <Navigate to="/vehicles" replace />;
}

function NavigateToAppraisalRecord() {
  return <Navigate to="/vehicles" replace />;
}

function NavigateToAppraisalRecordEdit() {
  return <Navigate to="/vehicles" replace />;
}

const autoRoutes = [
  { path: "/home", element: <HomePage /> },
  { path: "/dashboard", element: <DashboardPage /> },
  { path: "/appraisals", element: <Navigate to="/vehicles" replace /> },
  { path: "/appraisals/*", element: <Navigate to="/vehicles" replace /> },
  { path: "/merchandising", element: <Navigate to="/vehicles" replace /> },
  { path: "/merchandising/*", element: <Navigate to="/vehicles" replace /> },
  { path: "/vehicles", element: <VehiclesListPage /> },
  { path: "/customer-inquiries", element: <CustomerInquiriesPage /> },
  { path: "/reports", element: <ReportsPage /> },
  { path: "/site-settings", element: ownerAdmin(<SiteSettingsPage />) },
  { path: "/vehicles/new", element: ownerAdmin(<VehicleNewPage />) },
  { path: "/vehicles/:vehicleId/update", element: ownerAdmin(<VehicleNewPage />) },
  { path: "/vehicles/:vehicleId", element: <VehicleDetailPage /> },
  { path: "/dealer-profile", element: <Navigate to="/site-settings" replace /> },
  { path: "/assessments", element: <Navigate to="/appraisals" replace /> },
  { path: "/assessments/new", element: <Navigate to="/appraisals/new" replace /> },
  { path: "/assessments/:assessmentId/records/:recordId/edit", element: <NavigateToAppraisalRecordEdit /> },
  { path: "/assessments/:assessmentId/records/:recordId", element: <NavigateToAppraisalRecord /> },
  { path: "/assessments/:assessmentId/new", element: <NavigateToNewAppraisal /> },
  { path: "/assessments/:assessmentId/current", element: <NavigateToCurrentAppraisal /> },
  { path: "/assessments/:assessmentId", element: <NavigateToNewAppraisal /> },
  { path: "/staff", element: <Navigate to="/vehicles" replace /> },
  { path: "/staff/*", element: <Navigate to="/vehicles" replace /> },
  { path: "/team", element: <Navigate to="/vehicles" replace /> },
  { path: "/team/*", element: <Navigate to="/vehicles" replace /> },
  { path: "/attendance", element: <Navigate to="/vehicles" replace /> },
  { path: "/schedules", element: <CareStaffSchedulesPage /> },
  { path: "/timesheets", element: <Navigate to="/vehicles" replace /> },
  { path: "/my-attendance", element: <Navigate to="/vehicles" replace /> },
  { path: "/my-schedule", element: <Navigate to="/vehicles" replace /> },
  { path: "/my-timesheet", element: <Navigate to="/vehicles" replace /> },
  { path: "/hiring", element: <Navigate to="/vehicles" replace /> },
  { path: "/hiring/*", element: <Navigate to="/vehicles" replace /> },
];

const workspaceRoutes = [
  {
    path: "/workspaces",
    element: (
      <ShellRoute>
        <WorkspacesPage />
      </ShellRoute>
    ),
  },
  {
    path: "/workspaces/invitations/accept",
    element: (
      <ShellRoute>
        <WorkspaceInvitationAcceptPage />
      </ShellRoute>
    ),
  },
  {
    path: "/workspaces/:workspaceId",
    element: workspaceMemberOnly(
      <ShellRoute>
        <WorkspaceDetailsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/workspaces/:workspaceId/invitations/:invitationId",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceInvitations />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/members/:userId",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceMemberDetailsPage />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/organization",
    element: workspaceMemberOnly(
      <ShellRoute>
        <WorkspaceOrganizationPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/workspaces/current/payment-methods",
    element: ownerAdmin(<CurrentWorkspaceRedirect section="payment-methods" />),
  },
  {
    path: "/workspaces/current/charges",
    element: ownerAdmin(<CurrentWorkspaceRedirect section="charges" />),
  },
  {
    path: "/workspaces/current/subscriptions",
    element: ownerAdmin(<CurrentWorkspaceRedirect section="subscriptions" />),
  },
  {
    path: "/workspaces/:workspaceId/payment-methods",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspacePaymentMethodsPage />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/charges",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceChargesPage />
        </ShellRoute>,
      ),
    ),
  },
  {
    path: "/workspaces/:workspaceId/subscriptions",
    element: ownerAdmin(
      workspaceMemberOnly(
        <ShellRoute>
          <WorkspaceSubscriptionsPage />
        </ShellRoute>,
      ),
    ),
  },
];

const appUtilityRoutes = [
  {
    path: "/profile",
    element: (
      <ShellRoute>
        <UserProfilePage />
      </ShellRoute>
    ),
  },
  {
    path: "/profile/:userId",
    element: (
      <ShellRoute>
        <UserProfilePage />
      </ShellRoute>
    ),
  },
  {
    path: "/invitations",
    element: (
      <ShellRoute>
        <Invitations />
      </ShellRoute>
    ),
  },
  {
    path: "/website",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteDetails />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/templates",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/build/:templateId",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/regenerate",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect targetPanel="templates" />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/generating/:websiteId",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/success/:websiteId",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect />
      </ShellRoute>,
    ),
  },
  {
    path: "/website/domain",
    element: ownerAdmin(
      <ShellRoute>
        <WebsiteCreationRedirect targetPanel="domains" />
      </ShellRoute>,
    ),
  },
  {
    path: "/downloads",
    element: ownerAdmin(
      <ShellRoute>
        <WorkspaceDownloadsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/organizations",
    element: ownerAdmin(
      <ShellRoute>
        <OrganizationsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/organizations/:organizationId",
    element: ownerAdmin(
      <ShellRoute>
        <OrganizationDetailsPage />
      </ShellRoute>,
    ),
  },
  {
    path: "/access-control",
    element: ownerAdmin(
      <ShellRoute>
        <AccessControl />
      </ShellRoute>,
    ),
  },
  {
    path: "/settings",
    element: (
      <ShellRoute>
        <Settings />
      </ShellRoute>
    ),
  },
  {
    path: "/chat-space",
    element: (
      <ShellRoute>
        <ChatSpace />
      </ShellRoute>
    ),
  },
  {
    path: "/email-messages",
    element: (
      <ShellRoute>
        <EmailMessages />
      </ShellRoute>
    ),
  },
  {
    path: "/app/help",
    element: (
      <ShellRoute>
        <Help />
      </ShellRoute>
    ),
  },
  {
    path: "/support",
    element: (
      <ShellRoute>
        <Support />
      </ShellRoute>
    ),
  },
  {
    path: "/feedback",
    element: (
      <ShellRoute>
        <Feedback />
      </ShellRoute>
    ),
  },
  {
    path: "/partners",
    element: ownerAdmin(
      <ShellRoute>
        <Partners />
      </ShellRoute>,
    ),
  },
  {
    path: "/partners/commissions",
    element: ownerAdmin(
      <ShellRoute>
        <PartnerCommissions />
      </ShellRoute>,
    ),
  },
];

export const protectedRoutes = [
  ...autoRoutes,
  ...workspaceRoutes,
  ...appUtilityRoutes,
];
